import { Suspense, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function SupportPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const plans = [
    {
      name: 'ProCare Essential',
      tier: 'Entry-Tier Plan',
      description: 'Dependable annual maintenance for residential customers and small businesses',
      features: [
        'Annual system health check',
        'Essential remote support',
        'Discounts on upgrades and service',
        'Maintenance for cameras, sensors, readers, controllers, NVRs, panels, and power devices',
        'Affordable, protective, and value-driven service',
      ],
      color: 'slate',
    },
    {
      name: 'ProCare Plus',
      tier: 'Mid-Tier Plan',
      description: 'Enhanced reliability and optimization for larger installations requiring higher uptime',
      features: [
        'ALL Essential features included',
        'Semi-annual maintenance visits',
        'Component coverage (storage devices, UPS batteries, panel/sensor batteries, power supplies)',
        'Enhanced remote support with extended hours',
        'Next-business-day priority response',
        'Upgraded discount rates',
        'Reduced downtime and optimized performance',
      ],
      color: 'coral',
      popular: true,
    },
    {
      name: 'ProCare Elite',
      tier: 'Premium Tier Plan',
      description: 'White-glove service for high-security operations and 24/7 environments needing maximum uptime',
      features: [
        'ALL Plus features included',
        'Quarterly inspections',
        'Complete component coverage (cameras, sensors, readers, panels, PoE switches, NVR hardware)',
        '24/7 priority support with 4-hour emergency response',
        'Same-day service for critical failures',
        'Annual enhancement credit',
        'Advanced integration support (cloud, AI analytics, automation)',
        'Highest protection level with premium service',
      ],
      color: 'slate',
    },
  ];

  const responseTimeData = [
    {
      category: 'Emergency Response',
      essential: 'Next Business Day',
      plus: 'Next Business Day',
      elite: '4 Hours',
    },
    {
      category: 'Service Calls',
      essential: '48-72 Hours',
      plus: 'Next Business Day',
      elite: 'Same Day',
    },
    {
      category: 'Remote Support',
      essential: 'Business Hours',
      plus: 'Extended Hours',
      elite: '24/7 Priority',
    },
  ];

  const whyChooseFeatures = [
    {
      icon: 'ri-shield-check-line',
      title: 'Preventative Maintenance',
      description: 'Regular inspections and proactive care prevent costly failures and extend system lifespan',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Predictable Budgeting',
      description: 'Fixed monthly costs eliminate surprise repair bills and make planning easier',
    },
    {
      icon: 'ri-time-line',
      title: 'Faster Response Times',
      description: 'Priority support ensures your security systems are back online quickly when issues arise',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Higher System Uptime',
      description: 'Continuous monitoring and maintenance keep your security infrastructure operational 24/7',
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Improved Reliability',
      description: 'Expert care and regular updates ensure consistent performance and longevity',
    },
    {
      icon: 'ri-star-line',
      title: 'Enhanced Longevity',
      description: 'Professional maintenance maximizes your security investment and delays replacement costs',
    },
  ];

  const addOns = [
    {
      name: 'Cloud Services',
      description: 'Remote access, cloud storage, and advanced analytics for your security systems',
    },
    {
      name: 'Remote Support Pack',
      description: 'Extended remote diagnostics and troubleshooting beyond standard plan coverage',
    },
    {
      name: 'Extended Warranty',
      description: 'Additional equipment protection and replacement coverage for critical components',
    },
    {
      name: 'Training Sessions',
      description: 'On-site or virtual training for your team on system operation and best practices',
    },
  ];

  const faqs = [
    {
      question: 'What is included in a ProCare maintenance plan?',
      answer: 'ProCare plans include regular system inspections, software updates, technical support, equipment testing, and troubleshooting services. The specific services vary by plan tier—Essential provides annual maintenance, Plus offers semi-annual visits, and Elite includes quarterly inspections with 24/7 support.',
    },
    {
      question: 'How quickly can you respond to emergencies?',
      answer: 'Response times depend on your ProCare plan. Essential and Plus customers receive next-business-day response, while Elite customers get 24/7 priority support with 4-hour emergency response and same-day service for critical failures.',
    },
    {
      question: 'Can I upgrade my ProCare plan later?',
      answer: 'Yes! You can upgrade from Essential to Plus or Elite at any time. The new benefits will take effect immediately, and we\'ll prorate the cost difference for a seamless transition.',
    },
    {
      question: 'Do you service systems installed by other companies?',
      answer: 'Absolutely. Security Bay Area can provide ProCare maintenance and support for security systems installed by other providers. We\'ll perform an initial assessment to ensure compatibility and create a customized maintenance schedule.',
    },
    {
      question: 'What types of systems does ProCare cover?',
      answer: 'ProCare maintenance covers all security infrastructure including access control systems, surveillance cameras, intrusion detection, sensors, readers, controllers, NVRs, panels, PoE switches, power supplies, and related components. Our universal approach ensures comprehensive care regardless of system type.',
    },
    {
      question: 'Is there a contract commitment?',
      answer: 'We offer flexible terms for all ProCare plans. You can choose monthly, annual, or multi-year contracts. Annual contracts receive a 10% discount, and multi-year contracts receive up to 20% off.',
    },
  ];

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="bg-black min-h-screen">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center">
              <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-6">
                ProCare Maintenance Plans
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                Keep your security systems running at peak performance with Security Bay Area's comprehensive ProCare maintenance programs
              </p>
            </div>
          </div>
        </section>

        {/* Maintenance Plans */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-coral-50 rounded-full mb-6">
                <span className="text-coral-600 font-semibold text-sm">PROCARE MAINTENANCE PLANS</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Choose Your Protection Level
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Select the ProCare plan that best fits your security needs and operational requirements
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <div
                  key={index}
                  className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                    plan.popular ? 'ring-4 ring-coral-500' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute top-0 right-0 bg-coral-600 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                      MOST POPULAR
                    </div>
                  )}
                  <div className="p-8">
                    <div className="mb-2">
                      <span className="text-sm font-semibold text-coral-600 uppercase">{plan.tier}</span>
                    </div>
                    <h3 className="font-serif text-3xl font-bold text-slate-900 mb-4">
                      {plan.name}
                    </h3>
                    <p className="text-slate-600 mb-6">{plan.description}</p>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/maintenance-inquiry');
                      }}
                      className={`w-full py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                        plan.popular
                          ? 'bg-coral-600 text-white hover:bg-coral-700 shadow-lg hover:shadow-xl'
                          : 'bg-slate-800 text-white hover:bg-slate-900 shadow-md hover:shadow-lg'
                      }`}
                    >
                      Get Started
                    </button>
                  </div>
                  <div className="px-8 pb-8">
                    <div className="border-t border-slate-200 pt-6">
                      <ul className="space-y-4">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <i className="ri-check-line text-xl text-coral-600 mr-3 mt-0.5 flex-shrink-0"></i>
                            <span className="text-slate-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose ProCare */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl font-bold text-slate-900 mb-4">
                Why Choose ProCare?
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Professional maintenance that delivers measurable value and peace of mind
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {whyChooseFeatures.map((feature, index) => (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className={`${feature.icon} text-4xl text-coral-600`}></i>
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Response Time Comparison */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="font-serif text-4xl font-bold text-slate-900 mb-4">
                Response Time Comparison
              </h2>
              <p className="text-xl text-slate-600">
                See how each ProCare tier delivers support when you need it most
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">Service Type</th>
                      <th className="px-6 py-4 text-center font-semibold">Essential</th>
                      <th className="px-6 py-4 text-center font-semibold">Plus</th>
                      <th className="px-6 py-4 text-center font-semibold bg-coral-600">Elite</th>
                    </tr>
                  </thead>
                  <tbody>
                    {responseTimeData.map((row, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                        <td className="px-6 py-4 font-semibold text-slate-900">{row.category}</td>
                        <td className="px-6 py-4 text-center text-slate-700">{row.essential}</td>
                        <td className="px-6 py-4 text-center text-slate-700">{row.plus}</td>
                        <td className="px-6 py-4 text-center font-semibold text-coral-600">{row.elite}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Optional Add-Ons */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl font-bold text-slate-900 mb-4">
                Optional Add-Ons
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Enhance your ProCare plan with additional services tailored to your needs
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {addOns.map((addon, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-coral-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-add-circle-line text-2xl text-coral-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl text-slate-900 mb-2">{addon.name}</h3>
                      <p className="text-slate-600">{addon.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <div className="bg-slate-50 rounded-2xl p-8 max-w-3xl mx-auto">
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">
                  Easy Enrollment
                </h3>
                <p className="text-slate-600 mb-6">
                  Getting started with ProCare is simple. Contact our team to discuss your security infrastructure, and we'll recommend the perfect plan and add-ons for your needs. Activation is quick, and you'll have peace of mind knowing your systems are professionally maintained.
                </p>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/maintenance-inquiry')}
                  className="inline-flex items-center px-8 py-4 bg-coral-600 text-white rounded-full font-semibold text-lg hover:bg-coral-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  Enroll Now
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl font-bold text-slate-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-slate-600">
                Find answers to common questions about ProCare maintenance plans
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-md overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="font-semibold text-slate-900 pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-2xl text-slate-600 transition-transform duration-300 flex-shrink-0 ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="px-6 pb-5 text-slate-600">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-coral-600 to-coral-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Protect Your Investment?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Contact Security Bay Area today to discuss which ProCare plan is right for your security infrastructure
            </p>
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/maintenance-inquiry')}
              className="inline-flex items-center px-8 py-4 bg-white text-coral-600 rounded-full font-semibold text-lg hover:bg-slate-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap"
            >
              Contact Our Team
              <i className="ri-arrow-right-line ml-2 text-xl"></i>
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}
